import type { EnvProp, LevelDef } from './types';
import { roundedRectPath } from './geometry';

/**
 * NIVELES DEL ITINERARIO MANUAL, como datos. El bloque 1 (m1–m5) doma el
 * embrague y la caja; m6 es la cuesta, la prueba clásica; m7 el examen.
 * Coordenadas en metros sobre el plano XZ; heading 0° mira a +Z, 90° a +X.
 */

function lotWalls(w: number, d: number): EnvProp[] {
  const t = 0.6;
  const h = 0.9;
  return [
    { type: 'box', pos: [0, -d / 2], size: [w, h, t], tag: 'wall', color: '#8a8f98' },
    { type: 'box', pos: [0, d / 2], size: [w, h, t], tag: 'wall', color: '#8a8f98' },
    { type: 'box', pos: [-w / 2, 0], size: [t, h, d], tag: 'wall', color: '#8a8f98' },
    { type: 'box', pos: [w / 2, 0], size: [t, h, d], tag: 'wall', color: '#8a8f98' },
  ];
}

const REGION_EU_RIGHT = { drivingSide: 'right', signSet: 'eu' } as const;
const CLEAR = { type: 'clear', gripFactor: 1 } as const;

// ---------------------------------------------------------------------------
// M1 — El punto de fricción
// ---------------------------------------------------------------------------
export const LEVEL_M1: LevelDef = {
  id: 'm1-bite',
  order: 101,
  titleKey: 'level.m1.title',
  descriptionKey: 'level.m1.desc',
  track: 'manual',
  theoryKeys: ['theory.clutch', 'theory.bitepoint', 'theory.stalling'],
  car: 'm-city',
  region: REGION_EU_RIGHT,
  weather: CLEAR,
  spawn: { pos: [-30, 0], headingDeg: 90 },
  environment: {
    groundSize: [120, 90],
    props: [
      ...lotWalls(120, 90),
      { type: 'lane', points: [[-46, -2.6], [46, -2.6]], width: 0.15, color: 'white' },
      { type: 'lane', points: [[-46, 2.6], [46, 2.6]], width: 0.15, color: 'white' },
      { type: 'zone', id: 'speedAll', kind: 'speed', pos: [0, 0], size: [120, 90], limitKmh: 30, visible: false },
    ],
  },
  objectives: [
    { id: 'gear1', titleKey: 'obj.m1.gear1', type: 'shift_to', gear: 1 },
    { id: 'bite', titleKey: 'obj.m1.bite', type: 'bite_hold', seconds: 3 },
    { id: 'go', titleKey: 'obj.m1.go', type: 'move_off', minKmh: 8 },
    { id: 'stop', titleKey: 'obj.m1.stop', type: 'full_stop' },
    { id: 'neutral', titleKey: 'obj.m1.neutral', type: 'shift_to', gear: 'N' },
  ],
  pass: { maxHarshAccel: 2, maxHarshBrake: 2, maxCollisions: 0, maxTimeS: 300, maxStalls: 3 },
  unlocks: ['m2-moveoff'],
  xpReward: 100,
};

// ---------------------------------------------------------------------------
// M2 — Arrancar sin calarse (repeticiones)
// ---------------------------------------------------------------------------
export const LEVEL_M2: LevelDef = {
  id: 'm2-moveoff',
  order: 102,
  titleKey: 'level.m2.title',
  descriptionKey: 'level.m2.desc',
  track: 'manual',
  theoryKeys: ['theory.moveoff', 'theory.stalling'],
  car: 'm-city',
  region: REGION_EU_RIGHT,
  weather: CLEAR,
  spawn: { pos: [-42, 0], headingDeg: 90 },
  environment: {
    groundSize: [130, 90],
    props: [
      ...lotWalls(130, 90),
      { type: 'lane', points: [[-52, -2.6], [52, -2.6]], width: 0.15, color: 'white' },
      { type: 'lane', points: [[-52, 2.6], [52, 2.6]], width: 0.15, color: 'white' },
      { type: 'zone', id: 'speedAll', kind: 'speed', pos: [0, 0], size: [130, 90], limitKmh: 35, visible: false },
    ],
  },
  objectives: [
    { id: 'g1a', titleKey: 'obj.m2.gear1', type: 'shift_to', gear: 1 },
    { id: 'goA', titleKey: 'obj.m2.go15', type: 'move_off', minKmh: 15 },
    { id: 'stopA', titleKey: 'obj.m2.stop', type: 'full_stop' },
    { id: 'g1b', titleKey: 'obj.m2.again1', type: 'shift_to', gear: 1 },
    { id: 'goB', titleKey: 'obj.m2.go15', type: 'move_off', minKmh: 15 },
    { id: 'stopB', titleKey: 'obj.m2.stop', type: 'full_stop' },
    { id: 'g1c', titleKey: 'obj.m2.again2', type: 'shift_to', gear: 1 },
    { id: 'goC', titleKey: 'obj.m2.go20', type: 'move_off', minKmh: 20 },
    { id: 'stopC', titleKey: 'obj.m2.stop', type: 'full_stop' },
    { id: 'neutral', titleKey: 'obj.m2.neutral', type: 'shift_to', gear: 'N' },
  ],
  pass: { maxHarshAccel: 1, maxHarshBrake: 1, maxCollisions: 0, maxTimeS: 360, maxStalls: 2 },
  unlocks: ['m3-stops'],
  xpReward: 120,
};

// ---------------------------------------------------------------------------
// M3 — Detenerse sin calarse (embrague a tiempo)
// ---------------------------------------------------------------------------
function stopLineProps(x: number, suffix: string): EnvProp[] {
  return [
    { type: 'zone', id: `stop${suffix}`, kind: 'stop', pos: [x, 0], size: [1.6, 5.2] },
    { type: 'lane', points: [[x, -2.6], [x, 2.6]], width: 0.4, color: 'white' },
    { type: 'sign', pos: [x + 0.3, 3.6], rotYDeg: -90, sign: 'stop' },
  ];
}

export const LEVEL_M3: LevelDef = {
  id: 'm3-stops',
  order: 103,
  titleKey: 'level.m3.title',
  descriptionKey: 'level.m3.desc',
  track: 'manual',
  theoryKeys: ['theory.stopManual', 'theory.stalling'],
  car: 'm-city',
  region: REGION_EU_RIGHT,
  weather: CLEAR,
  spawn: { pos: [-50, 0], headingDeg: 90 },
  environment: {
    groundSize: [130, 90],
    props: [
      ...lotWalls(130, 90),
      { type: 'lane', points: [[-56, -2.6], [56, -2.6]], width: 0.15, color: 'white' },
      { type: 'lane', points: [[-56, 2.6], [56, 2.6]], width: 0.15, color: 'white' },
      ...stopLineProps(-10, 'A'),
      ...stopLineProps(20, 'B'),
      ...stopLineProps(45, 'C'),
      { type: 'zone', id: 'speedAll', kind: 'speed', pos: [0, 0], size: [130, 90], limitKmh: 40, visible: false },
    ],
  },
  objectives: [
    { id: 'g1', titleKey: 'obj.m3.gear1', type: 'shift_to', gear: 1 },
    { id: 'go1', titleKey: 'obj.m3.go', type: 'move_off', minKmh: 20 },
    { id: 'stopA', titleKey: 'obj.m3.stopA', type: 'full_stop', zoneId: 'stopA' },
    { id: 'go2', titleKey: 'obj.m3.go', type: 'move_off', minKmh: 20 },
    { id: 'stopB', titleKey: 'obj.m3.stopB', type: 'full_stop', zoneId: 'stopB' },
    { id: 'go3', titleKey: 'obj.m3.go25', type: 'move_off', minKmh: 25 },
    { id: 'stopC', titleKey: 'obj.m3.stopC', type: 'full_stop', zoneId: 'stopC' },
    { id: 'neutral', titleKey: 'obj.m3.neutral', type: 'shift_to', gear: 'N' },
  ],
  pass: { maxHarshAccel: 1, maxHarshBrake: 1, maxCollisions: 0, maxTimeS: 300, maxStalls: 1 },
  unlocks: ['m4-upshift'],
  xpReward: 140,
};

// ---------------------------------------------------------------------------
// M4 — Subir marchas con el tacómetro (y el oído)
// ---------------------------------------------------------------------------
export const LEVEL_M4: LevelDef = {
  id: 'm4-upshift',
  order: 104,
  titleKey: 'level.m4.title',
  descriptionKey: 'level.m4.desc',
  track: 'manual',
  theoryKeys: ['theory.hpattern', 'theory.upshift'],
  car: 'm-city',
  region: REGION_EU_RIGHT,
  weather: CLEAR,
  spawn: { pos: [-115, 0], headingDeg: 90 },
  environment: {
    groundSize: [260, 90],
    props: [
      ...lotWalls(260, 90),
      { type: 'lane', points: [[-122, -2.6], [122, -2.6]], width: 0.15, color: 'white' },
      { type: 'lane', points: [[-122, 2.6], [122, 2.6]], width: 0.15, color: 'white' },
      { type: 'zone', id: 'speedAll', kind: 'speed', pos: [0, 0], size: [260, 90], limitKmh: 70, visible: false },
    ],
  },
  objectives: [
    { id: 'g1', titleKey: 'obj.m4.gear1', type: 'shift_to', gear: 1 },
    { id: 'go', titleKey: 'obj.m4.go', type: 'move_off', minKmh: 15 },
    { id: 'g2', titleKey: 'obj.m4.gear2', type: 'shift_to', gear: 2 },
    { id: 'sp30', titleKey: 'obj.m4.sp30', type: 'reach_speed', minKmh: 30 },
    { id: 'g3', titleKey: 'obj.m4.gear3', type: 'shift_to', gear: 3 },
    { id: 'sp45', titleKey: 'obj.m4.sp45', type: 'reach_speed', minKmh: 45 },
    { id: 'g4', titleKey: 'obj.m4.gear4', type: 'shift_to', gear: 4 },
    { id: 'sp60', titleKey: 'obj.m4.sp60', type: 'reach_speed', minKmh: 58 },
    { id: 'stop', titleKey: 'obj.m4.stop', type: 'full_stop' },
    { id: 'neutral', titleKey: 'obj.m4.neutral', type: 'shift_to', gear: 'N' },
  ],
  pass: { maxHarshAccel: 1, maxHarshBrake: 1, maxCollisions: 0, maxTimeS: 240, maxStalls: 1, minGearScore: 55 },
  unlocks: ['m5-downshift', 'car:m-diesel'],
  xpReward: 160,
};

// ---------------------------------------------------------------------------
// M5 — Reducir marchas y freno motor
// ---------------------------------------------------------------------------
export const LEVEL_M5: LevelDef = {
  id: 'm5-downshift',
  order: 105,
  titleKey: 'level.m5.title',
  descriptionKey: 'level.m5.desc',
  track: 'manual',
  theoryKeys: ['theory.downshift', 'theory.revmatch'],
  car: 'm-city',
  region: REGION_EU_RIGHT,
  weather: CLEAR,
  spawn: { pos: [-115, 0], headingDeg: 90 },
  environment: {
    groundSize: [260, 90],
    props: [
      ...lotWalls(260, 90),
      { type: 'lane', points: [[-122, -2.6], [122, -2.6]], width: 0.15, color: 'white' },
      { type: 'lane', points: [[-122, 2.6], [122, 2.6]], width: 0.15, color: 'white' },
      { type: 'sign', pos: [8, 3.6], rotYDeg: -90, sign: 'speed30' },
      { type: 'zone', id: 'slow30', kind: 'speed', pos: [65, 0], size: [120, 12], limitKmh: 32 },
      { type: 'zone', id: 'stopEnd', kind: 'stop', pos: [104, 0], size: [1.8, 5.2] },
      { type: 'lane', points: [[104, -2.6], [104, 2.6]], width: 0.4, color: 'white' },
      { type: 'sign', pos: [104.3, 3.6], rotYDeg: -90, sign: 'stop' },
    ],
  },
  objectives: [
    { id: 'g1', titleKey: 'obj.m5.gear1', type: 'shift_to', gear: 1 },
    { id: 'go', titleKey: 'obj.m5.go', type: 'move_off', minKmh: 15 },
    { id: 'g3', titleKey: 'obj.m5.gear3', type: 'shift_to', gear: 3 },
    { id: 'sp50', titleKey: 'obj.m5.sp50', type: 'reach_speed', minKmh: 48 },
    { id: 'g4', titleKey: 'obj.m5.gear4', type: 'shift_to', gear: 4 },
    { id: 'g3b', titleKey: 'obj.m5.down3', type: 'shift_to', gear: 3, require: { maxSpeedKmh: 45 } },
    { id: 'g2b', titleKey: 'obj.m5.down2', type: 'shift_to', gear: 2, require: { maxSpeedKmh: 32 } },
    { id: 'stop', titleKey: 'obj.m5.stopLine', type: 'full_stop', zoneId: 'stopEnd' },
    { id: 'neutral', titleKey: 'obj.m5.neutral', type: 'shift_to', gear: 'N' },
  ],
  pass: {
    maxHarshAccel: 1,
    maxHarshBrake: 1,
    maxCollisions: 0,
    maxTimeS: 240,
    maxStalls: 1,
    minGearScore: 60,
    speedComplianceMin: 0.75,
  },
  unlocks: ['m6-hill'],
  xpReward: 180,
};

// ---------------------------------------------------------------------------
// M6 — Arranque en cuesta (freno de mano + punto de fricción)
// ---------------------------------------------------------------------------
const RAMP_PITCH = -7; // sube hacia +Z
const RAMP_LEN = 44;
const RAMP_THICK = 1.2;
/** Altura de la superficie de la rampa en la coordenada z dada (aprox.). */
const SIN7 = Math.sin((7 * Math.PI) / 180);
const rampTopY = (z: number, centerZ: number): number =>
  (z - centerZ) * SIN7 + (RAMP_THICK / 2);

export const LEVEL_M6: LevelDef = {
  id: 'm6-hill',
  order: 106,
  titleKey: 'level.m6.title',
  descriptionKey: 'level.m6.desc',
  track: 'manual',
  theoryKeys: ['theory.hillstart', 'theory.handbrakeM'],
  car: 'm-city',
  region: REGION_EU_RIGHT,
  weather: CLEAR,
  spawn: { pos: [0, -35], headingDeg: 0 },
  environment: {
    groundSize: [90, 150],
    props: [
      ...lotWalls(90, 150),
      // Carril de aproximación
      { type: 'lane', points: [[-2.6, -48], [-2.6, 22]], width: 0.15, color: 'white' },
      { type: 'lane', points: [[2.6, -48], [2.6, 22]], width: 0.15, color: 'white' },
      // Rampa que sube hacia +Z y meseta superior
      { type: 'ramp', pos: [0, 30], size: [12, RAMP_THICK, RAMP_LEN], rotYDeg: 0, pitchDeg: RAMP_PITCH },
      { type: 'box', pos: [0, 58], size: [12, 3.25, 13], tag: 'ramp', color: '#565b63' },
      // Zonas
      { type: 'zone', id: 'base', kind: 'target', pos: [0, 12], size: [7, 6] },
      {
        type: 'zone',
        id: 'slope',
        kind: 'stop',
        pos: [0, 38],
        size: [6, 6],
        y: rampTopY(38, 30) + 0.04,
        pitchDeg: RAMP_PITCH,
      },
      { type: 'zone', id: 'top', kind: 'target', pos: [0, 58], size: [8, 8], y: 3.3 },
      { type: 'zone', id: 'speedAll', kind: 'speed', pos: [0, 0], size: [90, 150], limitKmh: 35, visible: false },
    ],
  },
  objectives: [
    { id: 'g1', titleKey: 'obj.m6.gear1', type: 'shift_to', gear: 1, require: { glance: ['left', 'rear'] } },
    { id: 'base', titleKey: 'obj.m6.base', type: 'drive_to_zone', zoneId: 'base', require: { maxSpeedKmh: 30 } },
    { id: 'stopSlope', titleKey: 'obj.m6.stopSlope', type: 'full_stop', zoneId: 'slope' },
    { id: 'hill', titleKey: 'obj.m6.hill', type: 'hill_start', zoneId: 'slope', minKmh: 10, maxRollbackM: 0.4 },
    { id: 'top', titleKey: 'obj.m6.top', type: 'drive_to_zone', zoneId: 'top', require: { maxSpeedKmh: 30 } },
    { id: 'stopTop', titleKey: 'obj.m6.stopTop', type: 'full_stop' },
    { id: 'neutral', titleKey: 'obj.m6.neutral', type: 'shift_to', gear: 'N' },
  ],
  pass: { maxHarshAccel: 2, maxHarshBrake: 1, maxCollisions: 0, maxTimeS: 420, maxStalls: 2 },
  unlocks: ['m7-exam'],
  xpReward: 220,
};

// ---------------------------------------------------------------------------
// M7 — Examen del embrague: circuito con paradas
// ---------------------------------------------------------------------------
const M_CIRCUIT = roundedRectPath(40, 28, 12, 7);
const CIRCUIT_A = [...M_CIRCUIT.slice(0, 15), [40, -1] as [number, number]];
const CIRCUIT_B = [[40, -1] as [number, number], ...M_CIRCUIT.slice(15)];

export const LEVEL_M7: LevelDef = {
  id: 'm7-exam',
  order: 107,
  titleKey: 'level.m7.title',
  descriptionKey: 'level.m7.desc',
  track: 'manual',
  theoryKeys: ['theory.ecoShift', 'theory.msm'],
  car: 'm-city',
  region: REGION_EU_RIGHT,
  weather: CLEAR,
  spawn: { pos: [-40, 6], headingDeg: 0 },
  environment: {
    groundSize: [130, 100],
    props: [
      ...lotWalls(130, 100),
      { type: 'lane', points: M_CIRCUIT, width: 3.5, color: 'white' },
      { type: 'zone', id: 'stopZ', kind: 'stop', pos: [40, -1], size: [4.5, 1.8], rotYDeg: 0 },
      { type: 'lane', points: [[38.3, -1], [41.7, -1]], width: 0.4, color: 'white' },
      { type: 'sign', pos: [42.5, -0.6], rotYDeg: 180, sign: 'stop' },
      { type: 'zone', id: 'finish', kind: 'target', pos: [-40, -8], size: [7, 4] },
      { type: 'zone', id: 'speedAll', kind: 'speed', pos: [0, 0], size: [130, 100], limitKmh: 40, visible: false },
    ],
    paths: {
      circuitA: { points: CIRCUIT_A, width: 3.5 },
      circuitB: { points: CIRCUIT_B, width: 3.5 },
    },
  },
  objectives: [
    { id: 'g1', titleKey: 'obj.m7.gear1', type: 'shift_to', gear: 1, require: { glance: ['left', 'rear'] } },
    { id: 'lapA', titleKey: 'obj.m7.lapA', type: 'follow_path', pathId: 'circuitA', maxLateralM: 1.3, toZoneId: 'stopZ', require: { maxSpeedKmh: 40 } },
    { id: 'stop1', titleKey: 'obj.m7.stop1', type: 'full_stop', zoneId: 'stopZ' },
    { id: 'resume', titleKey: 'obj.m7.resume', type: 'move_off', minKmh: 10 },
    { id: 'lapB', titleKey: 'obj.m7.lapB', type: 'follow_path', pathId: 'circuitB', maxLateralM: 1.3, toZoneId: 'finish', require: { maxSpeedKmh: 40 } },
    { id: 'stop2', titleKey: 'obj.m7.stop2', type: 'full_stop' },
    { id: 'neutral', titleKey: 'obj.m7.neutral', type: 'shift_to', gear: 'N' },
  ],
  pass: {
    maxHarshAccel: 1,
    maxHarshBrake: 1,
    maxCollisions: 0,
    maxTimeS: 480,
    maxStalls: 1,
    minGearScore: 65,
    minClutchScore: 60,
    minOverallScore: 70,
    speedComplianceMin: 0.8,
  },
  unlocks: ['car:m-sport'],
  xpReward: 300,
};

// ---------------------------------------------------------------------------
// Explanada libre manual: rampas de 5° y 9°, eslalon y aparcamiento
// ---------------------------------------------------------------------------
function coneRow(x0: number, x1: number, z: number, step: number): EnvProp[] {
  const props: EnvProp[] = [];
  for (let x = x0; x <= x1 + 0.01; x += step) props.push({ type: 'cone', pos: [x, z] });
  return props;
}

export const FREE_MANUAL: LevelDef = {
  id: 'free-manual',
  order: 199,
  titleKey: 'level.freeManual.title',
  descriptionKey: 'level.freeManual.desc',
  track: 'manual',
  theoryKeys: [],
  car: 'm-city',
  region: REGION_EU_RIGHT,
  weather: CLEAR,
  spawn: { pos: [0, -40], headingDeg: 90 },
  environment: {
    groundSize: [150, 120],
    props: [
      ...lotWalls(150, 120),
      ...coneRow(-40, -12, -20, 4),
      // Rampa suave (5°) con meseta, al este
      { type: 'ramp', pos: [45, 14], size: [10, 1.2, 40], rotYDeg: 0, pitchDeg: -5 },
      { type: 'box', pos: [45, 40.5], size: [10, 2.3, 13], tag: 'ramp', color: '#565b63' },
      // Rampa exigente (9°) con meseta, al oeste
      { type: 'ramp', pos: [-45, 16], size: [10, 1.2, 30], rotYDeg: 0, pitchDeg: -9 },
      { type: 'box', pos: [-45, 37], size: [10, 2.95, 12], tag: 'ramp', color: '#565b63' },
      // Plaza de aparcamiento
      { type: 'lane', points: [[12.4, -42.9], [12.4, -48.3]], width: 0.12, color: 'white' },
      { type: 'lane', points: [[15.6, -42.9], [15.6, -48.3]], width: 0.12, color: 'white' },
      { type: 'lane', points: [[12.4, -48.3], [15.6, -48.3]], width: 0.12, color: 'white' },
    ],
  },
  objectives: [],
  pass: { maxHarshAccel: 99, maxHarshBrake: 99, maxCollisions: 999 },
  unlocks: [],
  xpReward: 0,
  freeRoam: true,
};

export const MANUAL_LEVELS: LevelDef[] = [
  LEVEL_M1,
  LEVEL_M2,
  LEVEL_M3,
  LEVEL_M4,
  LEVEL_M5,
  LEVEL_M6,
  LEVEL_M7,
  FREE_MANUAL,
];
